import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

export const isGroupMultiple = (group) => {
  if (!group) return false;
  if (typeof group.isMultiple !== "undefined") {
    return group.isMultiple === true || String(group.isMultiple).toLowerCase() === "true";
  }
  if (typeof group.multiple !== "undefined") {
    return group.multiple === true || String(group.multiple).toLowerCase() === "true";
  }
  return false;
};

export const useFilterOptions = ({
  filters = null,
  gender,
  modelType,
  onFilterChange,
}) => {
  const token = useSelector((state) => state.auth?.token);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});

  // Fallback state if `filters` prop is not passed directly
  const [fetchedFilters, setFetchedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Active left tab key
  const [activeTabKey, setActiveTabKey] = useState("");

  // Temporary & Applied filter states: { [filterKey]: [selectedValues] }
  const [tempFilters, setTempFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});

  const mapped_model_type =
    modelType === "mannequin" ? "mannequin_to_model" : "model_to_model";

  // Effective filters list (prop or fallback API response)
  const filterGroups = Array.isArray(filters) && filters.length > 0
    ? filters
    : fetchedFilters;

  // Set default active tab when filterGroups change
  useEffect(() => {
    if (filterGroups.length > 0) {
      const isCurrentActiveValid = filterGroups.some(
        (g) => (g.key || g.label?.toLowerCase()) === activeTabKey
      );
      if (!activeTabKey || !isCurrentActiveValid) {
        setActiveTabKey(filterGroups[0].key || filterGroups[0].label?.toLowerCase());
      }
    }
  }, [filterGroups, activeTabKey]);

  // Reset filter selections when gender or modelType changes
  useEffect(() => {
    setTempFilters({});
    setAppliedFilters({});
    setIsOpen(false);
  }, [gender, modelType]);

  // Fallback API fetch if `filters` prop is not provided
  useEffect(() => {
    if (Array.isArray(filters) && filters.length > 0) return;

    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.GET_FILTER_OPTIONS}?gender=${gender}&mode=${mapped_model_type}`,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const categories = response?.data?.data?.categories;
        if (Array.isArray(categories)) {
          const formattedCategories = categories
            .filter((c) => String(c).toLowerCase() !== "all")
            .map((c) => (typeof c === "object" ? c : { value: c, label: c }));

          setFetchedFilters([
            {
              label: "Category",
              key: "category",
              options: formattedCategories,
            },
          ]);
        } else {
          setError("Failed to fetch filter options");
          setFetchedFilters([]);
        }
      } catch (err) {
        console.error("Error fetching filter options:", err);
        setError(err.message);
        setFetchedFilters([]);
      } finally {
        setLoading(false);
      }
    };

    if (token && gender && modelType) {
      fetchFilterOptions();
    }
  }, [filters, gender, mapped_model_type, modelType, token]);

  // Popover positioning logic
  const updatePopoverPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const viewportPadding = 12;
    const gap = 6;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(360, window.innerWidth - viewportPadding * 2);
    const height = Math.min(260, window.innerHeight - viewportPadding * 2);
    const preferredLeft = buttonRect.right - width;
    const left = Math.min(
      Math.max(viewportPadding, preferredLeft),
      window.innerWidth - width - viewportPadding
    );
    const belowTop = buttonRect.bottom + gap;
    const aboveTop = buttonRect.top - height - gap;
    const top =
      belowTop + height <= window.innerHeight - viewportPadding
        ? belowTop
        : Math.max(viewportPadding, aboveTop);

    setPopoverStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight: height,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePopoverPosition();

    const handleScroll = (e) => {
      // Allow scrolling inside the filter popover (e.g. through tabs or options)
      if (
        popoverRef.current &&
        e.target instanceof Node &&
        popoverRef.current.contains(e.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, updatePopoverPosition]);

  // Sync temporary filters when popover opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters(appliedFilters);
    }
  }, [isOpen, appliedFilters]);

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Helper to normalize option items
  const normalizeOption = (opt) => {
    if (typeof opt === "object" && opt !== null) {
      return {
        value: String(opt.value ?? opt.label ?? ""),
        label: String(opt.label ?? opt.value ?? ""),
      };
    }
    return { value: String(opt), label: String(opt) };
  };

  // Option selection toggle handler (supports single vs multiple selection per group)
  const handleOptionToggle = (key, optValue) => {
    setTempFilters((prev) => {
      const currentSelected = prev[key] || [];
      const isSelected = currentSelected.includes(optValue);

      const group = filterGroups.find(
        (g) => (g.key || g.label?.toLowerCase()) === key
      );
      const isMulti = isGroupMultiple(group);

      let newSelected;

      if (isMulti) {
        // Multi-select: allow selecting multiple options simultaneously
        if (optValue.toLowerCase() === "all") {
          newSelected = isSelected ? [] : [optValue];
        } else {
          const cleanList = currentSelected.filter((v) => v.toLowerCase() !== "all");
          newSelected = isSelected
            ? cleanList.filter((v) => v !== optValue)
            : [...cleanList, optValue];
        }
      } else {
        // Single-select: only one option selected at a time
        newSelected = isSelected ? [] : [optValue];
      }

      return {
        ...prev,
        [key]: newSelected,
      };
    });
  };

  // Clear selections for active tab/mode only
  const handleClearActiveTab = (keyToClear) => {
    const key =
      keyToClear ||
      activeGroup?.key ||
      activeGroup?.label?.toLowerCase() ||
      activeTabKey;
    if (!key) return;

    setTempFilters((prev) => {
      const next = {
        ...prev,
        [key]: [],
      };
      if (activeGroup?.key && activeGroup.key !== key) {
        next[activeGroup.key] = [];
      }
      if (activeGroup?.label && activeGroup.label.toLowerCase() !== key) {
        next[activeGroup.label.toLowerCase()] = [];
      }
      return next;
    });
  };

  // For backward compatibility
  const handleClearFilters = handleClearActiveTab;

  // Apply all filters
  const handleApplyFilters = () => {
    setAppliedFilters(tempFilters);
    if (typeof onFilterChange === "function") {
      onFilterChange(tempFilters);
    }
    setIsOpen(false);
  };

  // Reset all filters
  const handleResetAll = () => {
    setTempFilters({});
    setAppliedFilters({});
    if (typeof onFilterChange === "function") {
      onFilterChange({});
    }
    setIsOpen(false);
  };

  // Toggle popover open state
  const toggleOpen = () => {
    if (!isOpen) updatePopoverPosition();
    setIsOpen((open) => !open);
  };

  // Calculate count of selected items for a specific key
  const getTabSelectedCount = (key) => {
    const group = filterGroups.find(
      (g) =>
        (g.key || g.label?.toLowerCase()) === key ||
        g.key === key ||
        g.label?.toLowerCase() === key
    );
    const list =
      tempFilters[key] ||
      (group?.key && tempFilters[group.key]) ||
      (group?.label && tempFilters[group.label.toLowerCase()]) ||
      [];
    return list.filter((v) => String(v).toLowerCase() !== "all").length;
  };

  // Total count of applied active filters across all categories
  const totalAppliedCount = Object.entries(appliedFilters).reduce(
    (acc, [_, vals]) => {
      if (!Array.isArray(vals)) return acc;
      return acc + vals.filter((v) => String(v).toLowerCase() !== "all").length;
    },
    0
  );

  const activeGroup = filterGroups.find(
    (g) => (g.key || g.label?.toLowerCase()) === activeTabKey
  ) || filterGroups[0];

  return {
    buttonRef,
    popoverRef,
    isOpen,
    setIsOpen,
    toggleOpen,
    popoverStyle,
    filterGroups,
    activeTabKey,
    setActiveTabKey,
    activeGroup,
    tempFilters,
    loading,
    error,
    totalAppliedCount,
    getTabSelectedCount,
    normalizeOption,
    handleOptionToggle,
    handleClearActiveTab,
    handleClearFilters,
    handleApplyFilters,
    handleResetAll,
    updatePopoverPosition,
    isGroupMultiple,
  };
};

export default useFilterOptions;
